/* eslint-disable arrow-body-style */
import { constants } from '../constants';
import {
  translateDescription,
  translateShortDescription,
  translateTitle,
} from '../language';

class ProductModel {
  constructor(data) {
    this.id = data.id;
    this.uid = data.uid;
    this.codename = data.codename;
    this.categoryId = data.categoryId;
    this.title = data.title;
    this.titleEN = data.titleEN;
    this.shortDescription = data.shortDescription;
    this.shortDescriptionEN = data.shortDescriptionEN;
    this.description = data.description;
    this.descriptionEN = data.descriptionEN;
    this.price = data.price;
    this.totalPrice = data.totalPrice;
    this.discountable = data.discountable;
    this.type = data.type;
    this.images = data.images;
    this.tags = data.tags;
    this.deliveryMethods = data.deliveryMethods;
    this.children = data.children;
    this.options = data.options;
    this.quantity = data.quantity || 1;
    this.isSplit = data.isSplit || false;
    this.nutrients = data.nutrients;
    this.minQuantity = data.minQuantity;

    this.init();
  }

  init() {
    if (!this.totalPrice) {
      this.calculateTotalPrice();
    }
  }

  getTitle() {
    return translateTitle(this);
  }

  getShortDescription() {
    return translateShortDescription(this);
  }

  getDescription() {
    return translateDescription(this);
  }

  hasImages() {
    return this.images && this.images.length > 0;
  }

  hasTag(codename) {
    if (this.tags) {
      return (this.tags.filter((t) => t.codename === codename).length > 0);
    }
    return false;
  }

  canSplit() {
    let r = false;
    if (this.options) {
      this.options.forEach((optionGroup) => {
        if (optionGroup.type === constants.OPTION_GROUP_TYPE_TOPPINGS
            && optionGroup.groups.find((group) => group.twoPart)) {
          r = true;
        }
      });
    }

    return r;
  }

  split() {
    if (this.canSplit()) {
      this.isSplit = true;
      this.splitOptions();
    }
  }

  splitOptions() {
    if (this.options) {
      this.options.forEach((optionGroup) => {
        optionGroup.groups.forEach((group) => {
          group.values.forEach((value) => {
            value.selected = { 0: value.selected, 1: null };
          });
        });
      });
    }
  }

  isValid() {
    let r = true;
    if (this.options) {
      this.options.forEach((optionGroup) => {
        // Check if minimum selection is met
        if (optionGroup.min > 0) {
          let selected = 0;
          optionGroup.groups.forEach((option) => {
            const values = this.findSelectedValues(option);
            values.forEach((value) => {
              selected += value.selected;
            });
          });

          if (selected < optionGroup.min) {
            r = false;
          }
        }
      });
    }

    return r;
  }

  setProtocol(url) {
    const protocol = process.env.RELEASE === 'development' ? 'http://' : 'https://';
    if (url && url.indexOf('//') === 0) {
      return url.replace('//', protocol);
    }

    return url;
  }

  getCoverImage() {
    const image = {
      src: null,
      srcset: '',
    };

    const sizes = [];

    if (this.images && this.images.length > 0) {
      const coverImage = this.images[0];
      image.src = this.setProtocol(coverImage.sizes.original);

      if (coverImage.sizes.xs) {
        image.srcset += `${this.setProtocol(coverImage.sizes.xs)} 600w,`;
        sizes.push('(max-width: 600px) 600px');
      }
      if (coverImage.sizes.sm) {
        image.srcset += `${this.setProtocol(coverImage.sizes.sm)} 1024w,`;
        sizes.push('(max-width: 1024px) 1024px');
      }
      if (coverImage.sizes.md) {
        image.srcset += `${this.setProtocol(coverImage.sizes.md)} 1440w,`;
        sizes.push('(max-width: 1440px) 1440px');
      }
      if (coverImage.sizes.lg) {
        image.srcset += `${this.setProtocol(coverImage.sizes.lg)} 1920w,`;
        sizes.push('1920px');
      }

      if (sizes.length > 0) {
        image.sizes = sizes.join(',');
      }
    }

    return image;
  }

  getOptionStringArray() {
    const strings = [];
    const selectedOptions = this.getSelectedOptions();

    if (this.isSplit) {
      const part0 = [];
      const part1 = [];
      selectedOptions.forEach((optionGroup) => {
        optionGroup.groups.forEach((option) => {
          option.values.forEach((value) => {
            if (value.selected[0] && value.selected[0] > 0) {
              let title = translateTitle(value);
              if (value.selected[0] > 1) {
                title += ` x${value.selected[0]}`;
              }
              part0.push(title);
            }

            if (value.selected[1] && value.selected[1] > 0) {
              let title = translateTitle(value);
              if (value.selected[1] > 1) {
                title += ` x${value.selected[1]}`;
              }
              part1.push(title);
            }
          });
        });
      });
      strings.push(part0.join(', '));
      strings.push(part1.join(', '));
    }
    else {
      selectedOptions.forEach((optionGroup) => {
        optionGroup.groups.forEach((option) => {
          const line = [];
          option.values.forEach((value) => {
            if (value.selected !== 0) {
              let title = translateTitle(value);
              if (value.selected > 1) {
                title += ` x${value.selected}`;
              }
              line.push(title);
            }
          });
          if (line.length) {
            strings.push(line.join(', '));
          }
        });
      });
    }

    return strings;
  }

  getOptionString() {
    const strings = this.getOptionStringArray();
    return strings.join(', ');
  }

  calculateTotalPrice() {
    let totalPriceImpact = 0;

    totalPriceImpact += this.calculateOptionsPrice(this.options);
    totalPriceImpact += this.calculateChildrenPrice();

    this.totalPrice = (this.price + totalPriceImpact) * this.quantity;
  }

  // eslint-disable-next-line class-methods-use-this
  calculateOptionsPrice(options) {
    let totalPriceImpact = 0;

    if (options) {
      // OptionGroup has min, max, included and type
      options.forEach((optionGroup) => {
        let included = 0;
        let processedQuantity = 0;

        if (optionGroup.included > 0) {
          included = optionGroup.included;
        }

        if (this.isSplit) {
          included *= 2;
        }

        // Option has type, priceImpact and twoPart
        if (optionGroup.type === constants.PRODUCT_TYPE_BUNDLE
            && this.hasTag(constants.BUNDLE_TYPE_3F2)) {
          // 3 for 2 bundle. Cheapest option is free
          optionGroup.groups.forEach((option) => {
            // OptionValue as value, selected, min, max, priceImpact
            const values = this.sortOptionValuesByPrice(this.findSelectedValues(option));
            values.forEach((value, index) => {
              let priceImpact = value.priceImpact ? value.priceImpact : option.priceImpact;

              if (index > 1) {
                value.modifiedPrice = 0;
                priceImpact = value.modifiedPrice;
              }
              else {
                // Reset the modified price
                value.modifiedPrice = null;
              }

              totalPriceImpact += priceImpact * value.selected;
            });
          });
        }
        else if (optionGroup.type === constants.PRODUCT_TYPE_BUNDLE
            && this.hasTag(constants.BUNDLE_TYPE_1N1K)) {
          // 1 + 1k bundle. Cheaper product costs 1k.
          optionGroup.groups.forEach((option) => {
            // OptionValue as value, selected, min, max, priceImpact
            const values = this.sortOptionValuesByPrice(this.findSelectedValues(option));
            values.forEach((value, index) => {
              let priceImpact = value.priceImpact ? value.priceImpact : option.priceImpact;

              if (index > 0) {
                value.modifiedPrice = 1000;
                priceImpact = value.modifiedPrice;
              }
              else {
                // Reset the modified price
                value.modifiedPrice = null;
              }

              totalPriceImpact += priceImpact * value.selected;
            });
          });
        }
        else {
          optionGroup.groups.forEach((option) => {
            // OptionValue as value, selected, min, max, priceImpact
            this.findSelectedValues(option).forEach((value) => {
              const priceImpact = value.priceImpact ? value.priceImpact : option.priceImpact;
              if (this.isSplit) {
                if (value.selected[0] && value.selected[0] > 0) {
                  for (let i = value.selected[0]; i > 0; i -= 1) {
                    processedQuantity += 1;
                    if (processedQuantity > included) {
                      totalPriceImpact += Math.round(priceImpact / 2);
                    }
                  }
                }
                if (value.selected[1] && value.selected[1] > 0) {
                  for (let i = value.selected[1]; i > 0; i -= 1) {
                    processedQuantity += 1;
                    if (processedQuantity > included) {
                      totalPriceImpact += Math.round(priceImpact / 2);
                    }
                  }
                }
              }
              else {
                for (let i = value.selected; i > 0; i -= 1) {
                  processedQuantity += 1;
                  if (processedQuantity > included) {
                    totalPriceImpact += priceImpact;
                  }
                }
              }
            });
          });
        }
      });
    }

    return totalPriceImpact;
  }

  calculateChildrenPrice() {
    let totalPriceImpact = 0;

    if (this.children) {
      this.children.forEach((child) => {
        if (child.options) {
          totalPriceImpact += this.calculateOptionsPrice(child.options);
        }
      });
    }

    return totalPriceImpact;
  }

  findSelectedValues(option) {
    return option.values.filter(
        (value) => (this.isSplit
            ? value.selected[0] > 0 || value.selected[1] > 0
            : value.selected > 0),
    );
  }

  flattenValues(values) {
    const array = [];

    values.forEach((value) => {
      for (let i = value.selected; i > 0; i -= 1) {
        array.push(value);
      }
    });

    return array;
  }

  sortOptionValuesByPrice(values) {
    return values.sort(
        (a, b) => ((a.priceImpact > b.priceImpact) ? -1 : ((b.priceImpact > a.priceImpact) ? 1 : 0)),
    );
  }

  updateOption(option) {
    const optionIndex = this.options.findIndex((o) => o.id === option.id);
    this.options[optionIndex] = option;
  }

  getSelectedOptions() {
    if (!this.options) {
      return [];
    }

    const options = JSON.parse(JSON.stringify(this.options));

    // Go through each option group and filter selected values
    options.forEach((optionGroup, optionGroupIndex) => {
      optionGroup.groups.forEach((option, optionIndex) => {
        options[optionGroupIndex].groups[optionIndex].values = this.findSelectedValues(option);
      });

      if (options[optionGroupIndex].groups.length < 1) {
        options.splice(optionGroupIndex, 1);
      }
    });

    return options;
  }

  // eslint-disable-next-line class-methods-use-this
  toOrderOptions(options) {
    const orderOptions = [];

    // Recreate all objects to include only required order data
    if (Array.isArray(options) && options.length > 0) {
      options.forEach((optionGroup) => {
        const orderGroupOptions = [];
        optionGroup.groups.forEach((option) => {
          const orderOptionValues = [];
          const orderOptionValuesPart0 = [];
          const orderOptionValuesPart1 = [];
          option.values.forEach((value) => {
            const priceImpact = value.modifiedPrice > -1 ? value.modifiedPrice : value.priceImpact;
            if (typeof value.selected !== 'object') {
              orderOptionValues.push({
                title: value.title,
                id: value.id,
                quantity: value.selected,
                priceImpact,
              });
            }
            else {
              if (value.selected[0] > 0) {
                orderOptionValuesPart0.push({
                  title: value.title,
                  id: value.id,
                  quantity: value.selected[0],
                  priceImpact,
                });
              }
              if (value.selected[1] > 0) {
                orderOptionValuesPart1.push({
                  title: value.title,
                  id: value.id,
                  quantity: value.selected[1],
                  priceImpact,
                });
              }
            }
          });

          if (orderOptionValues.length) {
            orderGroupOptions.push({
              title: option.title,
              id: option.id,
              priceImpact: option.priceImpact,
              values: orderOptionValues,
            });
          }

          if (orderOptionValuesPart0.length) {
            orderGroupOptions.push({
              title: option.title,
              id: option.id,
              priceImpact: option.priceImpact,
              part: 0,
              values: orderOptionValuesPart0,
            });
          }

          if (orderOptionValuesPart1.length) {
            orderGroupOptions.push({
              title: option.title,
              id: option.id,
              priceImpact: option.priceImpact,
              part: 1,
              values: orderOptionValuesPart1,
            });
          }
        });

        if (orderGroupOptions.length) {
          orderOptions.push({
            title: optionGroup.title,
            id: optionGroup.id,
            options: orderGroupOptions,
          });
        }
      });
    }

    return orderOptions;
  }

  toOrder(quantity) {
    const optionGroups = this.toOrderOptions(this.getSelectedOptions());
    return {
      debug: this.title,
      id: this.id,
      quantity: quantity || 1,
      optionGroups,
    };
  }
}

export default ProductModel;